import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { getUserQuizzes, type Quiz } from "@/lib/quiz-api";
import { Loader2, Plus } from "lucide-react";
import { Layout } from "@/components/layout";


export default function Quizzes() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  // Always define the query regardless of user state
  const { data: quizzes, isLoading } = useQuery<Quiz[]>({
    queryKey: ["/api/quiz/my"],
    queryFn: getUserQuizzes,
    enabled: !!user,
  });

  // Show create quiz option for unauthenticated users
  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-6 text-center mb-8">
            <p className="text-muted-foreground mb-4">
              Create your quiz now! You can sign in later to manage all your quizzes.
            </p>
            <Button onClick={() => setLocation("/create-quiz")}>
              Create New Quiz
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-serif text-gray-800">My Quiz</h1>
          <Button onClick={() => setLocation("/create-quiz")}>
            <Plus className="w-4 h-4 mr-2" />
            New Quiz
          </Button>
        </div>

        {quizzes && quizzes.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {quizzes.map((quiz) => (
              <Card 
                key={quiz.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setLocation(`/quiz/${quiz.internalId}`)}
              >
                <CardHeader className="pb-3">
                  <h2 className="text-xl font-semibold">{quiz.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    Share Code: <code className="bg-muted px-1 py-0.5 rounded">{quiz.shareCode}</code>
                  </p>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {(!quizzes || quizzes.length === 0) && (
          <Card className="p-6 text-center">
            <p className="text-muted-foreground">You haven't created any quizzes yet.</p>
          </Card>
        )}
      </div>
    </Layout>
  );
}